import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  getRecentIngestRuns,
  getLatestIngestRun,
  formatTimestamp,
  formatDuration,
} from "../services/samConfigService";
import { runIngest, runTestIngest, runDemoIngest, deleteDemoData } from "../services/ingestService";
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  FileText,
  AlertTriangle,
  Play,
  Loader2,
  Beaker,
  Database,
  Trash2,
} from "lucide-react";

const STATUS_CONFIG = {
  completed: {
    variant: "success",
    icon: CheckCircle,
    label: "Completed",
  },
  failed: {
    variant: "destructive",
    icon: XCircle,
    label: "Failed",
  },
  running: {
    variant: "default",
    icon: Activity,
    label: "Running",
  },
  pending: {
    variant: "secondary",
    icon: Clock,
    label: "Pending",
  },
};

export default function IngestStatus() {
  const [latestRun, setLatestRun] = useState(null);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ingest state
  const [isIngesting, setIsIngesting] = useState(false);
  const [ingestProgress, setIngestProgress] = useState(null);
  const [ingestLogs, setIngestLogs] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [latest, runs] = await Promise.all([
        getLatestIngestRun(),
        getRecentIngestRuns(10),
      ]);
      setLatestRun(latest);
      setRecentRuns(runs);
    } catch (err) {
      console.error("Error fetching ingest status:", err);
      setError("Failed to load ingest status");
    } finally {
      setLoading(false);
    }
  };

  const handleRunIngest = async (mode = "full") => {
    try {
      setIsIngesting(true);
      setIngestLogs([]);
      setIngestProgress({ status: "starting", message: "Initializing..." });

      let ingestFn;
      if (mode === "demo") {
        ingestFn = runDemoIngest;
      } else if (mode === "test") {
        ingestFn = runTestIngest;
      } else {
        ingestFn = runIngest;
      }

      await ingestFn({
        onProgress: (progress) => setIngestProgress(progress),
        onLog: (log) => setIngestLogs((prev) => [...prev.slice(-50), log]),
      });

      // Refresh data after successful ingest
      await fetchData();
    } catch (err) {
      console.error("Ingest error:", err);
      setIngestProgress({ status: "failed", message: err.message });
    } finally {
      setIsIngesting(false);
    }
  };

  const handleDeleteDemoData = async () => {
    if (!window.confirm("Are you sure you want to delete all demo/mock data? This will remove mock opportunities, awards, and related records.")) {
      return;
    }

    try {
      setIsIngesting(true);
      setIngestLogs([]);
      setIngestProgress({ status: "starting", message: "Deleting demo data..." });

      await deleteDemoData({
        onProgress: (progress) => setIngestProgress(progress),
        onLog: (log) => setIngestLogs((prev) => [...prev.slice(-50), log]),
      });

      // Refresh data after deletion
      await fetchData();
    } catch (err) {
      console.error("Delete error:", err);
      setIngestProgress({ status: "failed", message: err.message });
    } finally {
      setIsIngesting(false);
    }
  };

  const renderStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Loading ingest status...
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Latest Run Summary */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Latest Ingest Run</CardTitle>
              <CardDescription>
                Status of the most recent SAM.gov opportunity ingest
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center gap-2 text-destructive mb-4">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          )}

          {!latestRun ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No ingest runs found</p>
              <p className="text-sm mt-2">
                Configure NAICS codes in Settings, then run an ingest job
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Status</p>
                {renderStatusBadge(latestRun.status)}
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Started</p>
                <p className="font-medium">
                  {formatTimestamp(latestRun.started_at)}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="font-medium">
                  {formatDuration(latestRun.duration_ms)}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Records</p>
                <p className="font-medium">
                  {latestRun.records_processed || 0} processed
                </p>
              </div>
            </div>
          )}

          {latestRun && (
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Actions Fetched</CardDescription>
                  <CardTitle className="text-2xl">
                    {latestRun.actions_fetched || 0}
                  </CardTitle>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Opportunities Created</CardDescription>
                  <CardTitle className="text-2xl text-green-600">
                    {latestRun.opportunities_created || 0}
                  </CardTitle>
                </CardHeader>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Errors</CardDescription>
                  <CardTitle className="text-2xl text-destructive">
                    {latestRun.errors?.length || 0}
                  </CardTitle>
                </CardHeader>
              </Card>
            </div>
          )}

          {latestRun?.errors?.length > 0 && (
            <div className="mt-4 p-4 bg-destructive/10 rounded-lg">
              <p className="font-medium text-destructive mb-2">Errors:</p>
              <ul className="list-disc list-inside text-sm space-y-1">
                {latestRun.errors.slice(0, 5).map((err, i) => (
                  <li key={i} className="text-destructive">
                    {err}
                  </li>
                ))}
                {latestRun.errors.length > 5 && (
                  <li className="text-muted-foreground">
                    ... and {latestRun.errors.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Runs History */}
      <Card>
        <CardHeader>
          <CardTitle>Ingest History</CardTitle>
          <CardDescription>
            Recent SAM.gov opportunity ingest runs
          </CardDescription>
        </CardHeader>
        <CardContent>
          {recentRuns.length === 0 ? (
            <p className="text-center py-4 text-muted-foreground">
              No ingest history available
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Started</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                  <TableHead className="text-right">Opportunities</TableHead>
                  <TableHead className="text-right">Duration</TableHead>
                  <TableHead className="text-right">Errors</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentRuns.map((run) => (
                  <TableRow key={run.id}>
                    <TableCell className="font-medium">
                      {formatTimestamp(run.started_at)}
                    </TableCell>
                    <TableCell>{renderStatusBadge(run.status)}</TableCell>
                    <TableCell className="text-right">
                      {run.actions_fetched || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      {run.opportunities_created || 0}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatDuration(run.duration_ms)}
                    </TableCell>
                    <TableCell className="text-right">
                      {run.errors?.length || 0}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Run Ingest */}
      <Card>
        <CardHeader>
          <CardTitle>Run Ingest</CardTitle>
          <CardDescription>
            Fetch opportunities from SAM.gov and compute similarity scores
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Demo Mode Notice */}
          <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <p className="text-sm text-yellow-700">
              <strong>Note:</strong> Use "Load Demo Data" to test the UI with sample data.
              For live SAM.gov data, you need a valid{" "}
              <a
                href="https://sam.gov/profile/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                SAM.gov Opportunities API key
              </a>
              .
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button
              onClick={() => handleRunIngest("demo")}
              disabled={isIngesting}
              variant="default"
              className="bg-green-600 hover:bg-green-700"
            >
              {isIngesting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Database className="h-4 w-4 mr-2" />
              )}
              Load Demo Data (25 records)
            </Button>
            <Button
              onClick={() => handleRunIngest("test")}
              disabled={isIngesting}
              variant="outline"
            >
              {isIngesting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Beaker className="h-4 w-4 mr-2" />
              )}
              Test SAM.gov API
            </Button>
            <Button
              onClick={() => handleRunIngest("full")}
              disabled={isIngesting}
              variant="outline"
            >
              {isIngesting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Play className="h-4 w-4 mr-2" />
              )}
              Run Full Ingest
            </Button>
            <Button
              onClick={handleDeleteDemoData}
              disabled={isIngesting}
              variant="destructive"
            >
              {isIngesting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Demo Data
            </Button>
          </div>

          {/* Progress indicator */}
          {ingestProgress && (
            <div
              className={`p-4 rounded-lg ${
                ingestProgress.status === "failed"
                  ? "bg-destructive/10 text-destructive"
                  : ingestProgress.status === "completed"
                  ? "bg-green-500/10 text-green-700"
                  : "bg-blue-500/10 text-blue-700"
              }`}
            >
              <div className="flex items-center gap-2">
                {ingestProgress.status === "running" && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                {ingestProgress.status === "completed" && (
                  <CheckCircle className="h-4 w-4" />
                )}
                {ingestProgress.status === "failed" && (
                  <XCircle className="h-4 w-4" />
                )}
                <span className="font-medium">{ingestProgress.message}</span>
              </div>
            </div>
          )}

          {/* Logs */}
          {ingestLogs.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Logs:</p>
              <div className="bg-muted p-3 rounded-lg max-h-40 overflow-y-auto text-xs font-mono">
                {ingestLogs.map((log, i) => (
                  <div
                    key={i}
                    className={
                      log.level === "error"
                        ? "text-destructive"
                        : log.level === "warn"
                        ? "text-yellow-600"
                        : "text-muted-foreground"
                    }
                  >
                    [{log.level}] {log.message}
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Note: Full ingest fetches all configured NAICS codes from SAM.gov.
            For production, schedule via Cloud Functions.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
